import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useCart } from '../contexts/CartContext';
import { useSettings } from '../contexts/SettingsContext';
import { Trash2, Plus, Minus, ShoppingBag } from 'lucide-react';

// Image URL helper function - HTTPS uyumlu
const getImageUrl = (imagePath) => {
  if (!imagePath) return '';
  
  // Production'da HTTPS kullan
  if (process.env.NODE_ENV === 'production') {
    // HTTPS ile başlamıyorsa HTTPS ekle
    if (imagePath.startsWith('http://')) {
      return imagePath.replace('http://', 'https://');
    }
    // HTTPS ile başlıyorsa direkt döndür
    if (imagePath.startsWith('https://')) {
      return imagePath;
    }
    // Relative path ise HTTPS domain ile birleştir
    if (imagePath.startsWith('/')) {
      return `https://leasertech.com${imagePath}`;
    }
    return `https://leasertech.com/${imagePath}`;
  }
  
  // Development'ta localhost kullan
  const API_BASE_URL = 'http://localhost:5000';
  if (imagePath.startsWith('http')) {
    return imagePath;
  }
  return `${API_BASE_URL}${imagePath}`;
};

const Cart = () => {
  const { items, totalItems, totalPrice, loading, updateCartItem, removeFromCart, clearCart } = useCart();
  const { settings } = useSettings();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleQuantityChange = async (productId, newQuantity) => {
    if (newQuantity < 0) return;
    await updateCartItem(productId, newQuantity);
  };

  const handleRemoveItem = async (productId) => {
    await removeFromCart(productId);
  };

  const handleClearCart = async () => {
    if (window.confirm('Sepeti tamamen temizlemek istediğinizden emin misiniz?')) {
      await clearCart();
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
          </div>
        </div>
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-8">
            Sepetim
          </h1>
          <div className="bg-white rounded-lg shadow-md p-12 text-center">
            <ShoppingBag className="h-24 w-24 text-gray-300 mx-auto mb-6" />
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">
              Sepetiniz boş
            </h2>
            <p className="text-gray-600 mb-8">
              Henüz sepetinize ürün eklemediniz. Alışverişe başlamak için ürünleri inceleyin.
            </p>
            <Link
              to="/products"
              className="inline-flex items-center px-6 py-3 bg-primary-600 text-white font-medium rounded-lg hover:bg-primary-700 transition-colors"
            >
              Alışverişe Başla
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-8 space-y-4 sm:space-y-0">
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">
            Sepetim ({totalItems} ürün)
          </h1>
          <button
            onClick={handleClearCart}
            className="text-red-600 hover:text-red-700 font-medium flex items-center space-x-2 text-sm sm:text-base"
          >
            <Trash2 className="h-4 w-4 sm:h-5 sm:w-5" />
            <span>Sepeti Temizle</span>
          </button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Sepet Ürünleri */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-lg shadow-md">
              {items.map((item) => (
                <div key={item.product_id} className="p-4 sm:p-6 border-b border-gray-200 last:border-b-0">
                  <div className="flex flex-col sm:flex-row items-start sm:items-center space-y-4 sm:space-y-0 sm:space-x-4">
                    {/* Ürün Resmi */}
                    <div className="flex-shrink-0 mx-auto sm:mx-0">
                      <img
                        src={getImageUrl(item.product.images[0]?.url)}
                        alt={item.product.name}
                        className="w-16 h-16 sm:w-20 sm:h-20 object-contain rounded-lg border"
                        crossOrigin="anonymous"
                        onError={(e) => {
                          console.error('Görsel yüklenemedi:', item.product.name, item.product.images[0]?.url);
                          e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSIjZjNmNGY2Ii8+PHRleHQgeD0iNTAlIiB5PSI1MCUiIGZvbnQtZmFtaWx5PSJBcmlhbCIgZm9udC1zaXplPSIxNCIgZmlsbD0iIzk5YTNhZiIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZHk9Ii4zZW0iPkdyw7xzw7xsIFlva8SxPC90ZXh0Pjwvc3ZnPg==';
                        }}
                      />
                    </div>

                    {/* Ürün Bilgileri */}
                    <div className="flex-1 min-w-0 text-center sm:text-left">
                      <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-2">
                        {item.product.name}
                      </h3>
                      <p className="text-sm sm:text-base text-gray-600 mb-1">
                        {item.product.price.toLocaleString('tr-TR')} TL
                      </p>
                      <p className="text-xs sm:text-sm text-gray-500">
                        Stok: {item.product.stock} adet
                      </p>
                    </div>

                    {/* Miktar Kontrolü ve Fiyat - Mobilde alt satırda */}
                    <div className="flex items-center justify-between w-full sm:w-auto sm:justify-center space-x-4">
                      {/* Miktar Kontrolü */}
                      <div className="flex items-center space-x-3">
                        <button
                          onClick={() => handleQuantityChange(item.product_id, item.quantity - 1)}
                          disabled={item.quantity <= 1}
                          className="w-8 h-8 rounded-full border border-gray-300 flex items-center justify-center hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <Minus className="h-4 w-4" />
                        </button>
                        <span className="w-12 text-center font-medium">
                          {item.quantity}
                        </span>
                        <button
                          onClick={() => handleQuantityChange(item.product_id, item.quantity + 1)}
                          disabled={item.quantity >= item.product.stock}
                          className="w-8 h-8 rounded-full border border-gray-300 flex items-center justify-center hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <Plus className="h-4 w-4" />
                        </button>
                      </div>

                      {/* Toplam Fiyat */}
                      <div className="text-right sm:text-center">
                        <p className="text-base sm:text-lg font-semibold text-gray-900">
                          {(item.product.price * item.quantity).toLocaleString('tr-TR')} TL
                        </p>
                      </div>

                      {/* Sil Butonu */}
                      <button
                        onClick={() => handleRemoveItem(item.product_id)}
                        className="text-red-600 hover:text-red-700 p-2"
                      >
                        <Trash2 className="h-5 w-5" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Sipariş Özeti */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg shadow-md p-6 sticky top-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">
                Sipariş Özeti
              </h2>
              
              <div className="space-y-3 mb-6">
                <div className="flex justify-between">
                  <span className="text-gray-600">Ürünler ({totalItems})</span>
                  <span className="font-medium">
                    {totalPrice.toLocaleString('tr-TR')} TL
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Kargo</span>
                  <span className="font-medium text-green-600">
                    {totalPrice >= (settings.shipping?.freeShippingThreshold || 300) 
                      ? 'Ücretsiz' 
                      : `${(settings.shipping?.defaultShippingCost || 49).toFixed(2)} TL`
                    }
                  </span>
                </div>
                <div className="border-t border-gray-200 pt-3">
                  <div className="flex justify-between">
                    <span className="text-lg font-semibold text-gray-900">Toplam</span>
                    <span className="text-lg font-semibold text-gray-900">
                      {(totalPrice + (totalPrice >= (settings.shipping?.freeShippingThreshold || 300) 
                        ? 0 
                        : (settings.shipping?.defaultShippingCost || 49))
                      ).toLocaleString('tr-TR')} TL
                    </span>
                  </div>
                </div>
              </div>

              {/* Kabul Edilen Ödeme Yöntemleri */}
              <div className="mb-6 pt-4 border-t border-gray-200">
                <div className="flex flex-col gap-3">
                  <span className="text-xs text-gray-600 font-medium text-center">Kabul Edilen Ödeme Yöntemleri:</span>
                  <div className="flex items-center justify-center flex-wrap gap-3">
                    {/* Visa Logo */}
                    <img 
                      src="/images/visa.svg" 
                      alt="Visa" 
                      className="h-6 object-contain flex-shrink-0"
                      onError={(e) => {
                        e.target.style.display = 'none';
                      }}
                    />
                    {/* MasterCard Logo */}
                    <img 
                      src="/images/mastercard.png" 
                      alt="MasterCard" 
                      className="h-6 object-contain flex-shrink-0"
                      onError={(e) => {
                        e.target.style.display = 'none';
                      }}
                    />
                    {/* iyzico Logo */}
                    <img 
                      src="/images/iyzico_logo_band.svg" 
                      alt="iyzico ile öde" 
                      className="h-6 sm:h-8 object-contain max-w-full flex-shrink"
                      style={{ maxWidth: '200px' }}
                      onError={(e) => {
                        e.target.style.display = 'none';
                      }}
                    />
                  </div>
                </div>
              </div>

              <Link
                to="/checkout"
                className="w-full bg-primary-600 text-white py-3 px-6 rounded-lg font-medium hover:bg-primary-700 transition-colors text-center block"
              >
                Siparişi Tamamla
              </Link>

              <Link
                to="/products"
                className="w-full bg-gray-100 text-gray-700 py-3 px-6 rounded-lg font-medium hover:bg-gray-200 transition-colors text-center block mt-3"
              >
                Alışverişe Devam Et
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Cart;

